// AP-120 · Assistente IA JMS — Chat com streaming SSE + escalação para ticket
// AP-152 · usa `token` do PortalAuthContext.
// AP-168 · Após escalação, mostra um card estruturado de sucesso com
// Número, Assunto, Data/Hora, Status inicial e CTA para "Meus Tickets".
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Bot, Loader2, LifeBuoy, User, FolderOpen, CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePortalNotifications } from "../../context/PortalNotificationsContext";
import { usePortalI18n } from "./PortalI18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// AP-168 · Card estruturado de ticket criado, embutido no fluxo do chat.
function TicketCreatedCard({ ticket }) {
  const { t } = usePortalI18n();
  const navigate = useNavigate();
  return (
    <div data-testid="assistant-ticket-created-card"
         className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 space-y-3 shadow-sm">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-emerald-900 text-sm">
            {t("assistant.ticketCreatedTitle")}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div>
          <dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            {t("assistant.ticketNumber")}
          </dt>
          <dd className="font-mono font-bold text-emerald-900" data-testid="assistant-ticket-number">
            #{ticket.ticket_number}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            {t("assistant.ticketInitialStatus")}
          </dt>
          <dd className="font-semibold text-emerald-900" data-testid="assistant-ticket-status">
            {t("tickets.status.open")}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            {t("assistant.ticketSubject")}
          </dt>
          <dd className="text-emerald-950" data-testid="assistant-ticket-subject">
            {ticket.subject}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            {t("assistant.ticketDate")}
          </dt>
          <dd className="text-emerald-950 tabular-nums" data-testid="assistant-ticket-date">
            {fmtDT(ticket.created_at)}
          </dd>
        </div>
      </dl>

      <p className="text-[11px] text-emerald-800">
        {t("assistant.ticketFooter")}
      </p>

      <div className="pt-1">
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate("/portal/tickets")}
                data-testid="assistant-ticket-view-cta">
          <FolderOpen className="h-4 w-4 mr-1" /> {t("assistant.ticketCta")}
        </Button>
      </div>
    </div>
  );
}

export default function PortalAssistant() {
  const { token } = usePortalAuth();
  const { t } = usePortalI18n();
  const { refresh: refreshNotifications } = usePortalNotifications();
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "👋 Bonjou! Mwen se Asistan IA JMS. Kijan mwen ka ede w jodi a?",
  }]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [lastFailed, setLastFailed] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setLastFailed(false);
    setMessages(m => [...m, { role: "user", content: msg },
                       { role: "assistant", content: "", streaming: true }]);
    setStreaming(true);
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const r = await fetch(`${BACKEND}/api/portal/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                    Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
        signal: controller.signal,
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.split("\n").find(l => l.startsWith("data:"));
          if (!line) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.type === "meta" && payload.session_id) {
              setSessionId(payload.session_id);
            } else if (payload.type === "delta") {
              setMessages(m => {
                const last = m[m.length - 1];
                if (last?.role !== "assistant") return m;
                return [...m.slice(0, -1),
                        { ...last, content: last.content + payload.content }];
              });
            } else if (payload.type === "ticket_created") {
              // AP-169 · Backend criou o ticket DE VERDADE durante o streaming.
              const tk = payload.ticket || {};
              toast.success(t("assistant.ticketToast").replace("{n}", tk.ticket_number || ""));
              setMessages(m => [...m, {
                role: "assistant",
                kind: "ticket_card",
                ticket: tk,
                streaming: false,
              }]);
              try { refreshNotifications && refreshNotifications(); } catch {}
            } else if (payload.type === "ticket_error") {
              // AP-169 · falhou ao persistir — mostra erro honesto no chat.
              toast.error(t("assistant.escalateFailed").replace("{err}", payload.error || "insert_failed"));
              setMessages(m => [...m, {
                role: "assistant",
                content: t("assistant.escalateFailed").replace("{err}", payload.error || "insert_failed"),
                streaming: false,
              }]);
            } else if (payload.type === "error") {
              setLastFailed(true);
              setMessages(m => {
                const last = m[m.length - 1];
                return [...m.slice(0, -1), {
                  ...last,
                  content: "Mwen pa rive rezoud kestyon sa a. Ou ka voye demann ou bay Ekip JMS la.",
                  streaming: false }];
              });
            } else if (payload.type === "done") {
              setMessages(m => {
                const last = m[m.length - 1];
                return [...m.slice(0, -1), { ...last, streaming: false,
                        model: payload.model }];
              });
            }
          } catch (e) { /* ignore parse err */ }
        }
      }
    } catch (e) {
      setLastFailed(true);
      setMessages(m => [...m.slice(0, -1),
        { role: "assistant",
          content: "Mwen pa rive rezoud kestyon sa a. Ou ka voye demann ou bay Ekip JMS la.",
          streaming: false }]);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const escalate = async () => {
    if (escalating) return;
    setEscalating(true);
    try {
      // AP-168 · passa a última mensagem do cliente como "reason" para que o
      // assunto do ticket fique informativo mesmo quando o backend não achar
      // a conversa persistida.
      const lastUserMsg = [...messages].reverse().find(
        (m) => m.role === "user" && m.content
      )?.content || "";
      const reason = lastUserMsg || "Cliente solicitou atendimento humano";
      const r = await fetch(`${BACKEND}/api/portal/assistant/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                    Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId,
                                reason,
                                subject: lastUserMsg.slice(0, 140) || undefined }),
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      const data = await r.json();
      // AP-168 · Toast + card estruturado no stream da conversa.
      toast.success(t("assistant.ticketToast").replace("{n}", data.ticket_number || ""));
      setMessages(m => [...m, {
        role: "assistant",
        kind: "ticket_card",
        ticket: data,
        streaming: false,
      }]);
      setLastFailed(false);
      // Atualiza o badge do sino (nova portal_notification criada no backend).
      try { refreshNotifications && refreshNotifications(); } catch { /* silent */ }
    } catch (e) {
      toast.error(t("assistant.escalateFailed").replace("{err}", e.message || "network"));
      console.error("[escalate]", e);
    } finally { setEscalating(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-160px)]"
         data-testid="portal-assistant">
      <div className="flex items-center gap-3 pb-3 border-b">
        <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center text-primary">
          <Bot className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-bold">🤖 Asistan IA JMS</h2>
          <p className="text-[11px] text-muted-foreground">Asistans 24/7 · Kreyòl · Português · Français · Español · English</p>
        </div>
      </div>

      <div ref={boxRef} className="flex-1 overflow-y-auto py-3 space-y-3 pr-1"
           data-testid="portal-assistant-messages">
        {messages.map((m, i) => (
          <div key={i}
               className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="h-7 w-7 rounded-full bg-primary/10 grid place-items-center text-primary shrink-0">
                <Bot className="h-4 w-4" />
              </div>
            )}
            {/* AP-168 · Mensagem especial — card do ticket criado. */}
            {m.kind === "ticket_card" && m.ticket ? (
              <div className="max-w-[90%] flex-1">
                <TicketCreatedCard ticket={m.ticket} />
              </div>
            ) : (
              <div className={`rounded-2xl px-3 py-2 max-w-[80%] text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-primary text-primary-foreground"
                                      : "bg-slate-100 text-slate-900"}`}>
                {m.streaming && !m.content && (
                  <span className="text-slate-500 italic">🤖 IA JMS ap verifye dosye ou…</span>
                )}
                {m.content}
                {m.streaming && m.content && <Loader2 className="h-3 w-3 animate-spin inline ml-1" />}
              </div>
            )}
            {m.role === "user" && (
              <div className="h-7 w-7 rounded-full bg-slate-200 grid place-items-center shrink-0">
                <User className="h-4 w-4 text-slate-600" />
              </div>
            )}
          </div>
        ))}
        {lastFailed && !streaming && (
          <div className="pl-9">
            <Button onClick={escalate} disabled={escalating} variant="secondary"
                    className="text-xs" data-testid="portal-assistant-escalate">
              {escalating ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> N ap kreye tikè…</>
                             : <><LifeBuoy className="h-3 w-3 mr-1" /> 📩 Voye bay Ekip JMS</>}
            </Button>
          </div>
        )}
      </div>

      <div className="border-t pt-2 flex gap-2 items-end">
        <Textarea value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault(); send(); } }}
                    placeholder="Ekri kesyon w lan..."
                    disabled={streaming}
                    className="min-h-[44px] max-h-32 resize-none text-sm"
                    data-testid="portal-assistant-input" />
        <Button onClick={send} disabled={!input.trim() || streaming}
                data-testid="portal-assistant-send">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      {!lastFailed && !streaming && messages.length >= 2 && (
        <div className="text-center pt-2">
          <button onClick={escalate} disabled={escalating}
                  className="text-[10px] text-muted-foreground hover:text-primary underline"
                  data-testid="portal-assistant-escalate-hint">
            Pale ak yon moun nan ekip JMS
          </button>
        </div>
      )}
    </div>
  );
}
